import {
    FormComponentFieldAttributes,
    FormComponentFieldProps,
} from "components/Form/components/FormComponentProps";
import { Icon } from "components/Icon/Icon";
import { IconCollection } from "components/Icon/IconCollection";
import { DocsInfoTooltip } from "components/Tooltip/DocsInfoTooltip";
import Tooltip from "components/Tooltip/Tooltip";
import { PropsWithChildren } from "react";
import { FieldInputProps, FieldMetaState } from "react-final-form";
import "./Input.scss";

interface InputProps {
    input: FieldInputProps<any>;
    fieldProps?: FormComponentFieldProps;
    fieldAttr?: FormComponentFieldAttributes;
    meta?: FieldMetaState<any>;
    className?: string;
}

export function Input({
    input,
    fieldProps = {},
    fieldAttr = {},
    meta = {},
    children,
    className,
}: PropsWithChildren<InputProps>) {
    const hasValue =
        (input.value && (input.value.length > 0 || input.value)) ||
        input.value === 0 ||
        input.value === false;
    const labelActive = meta.active || hasValue;
    const hasError = (fieldProps?.validateOnMount || meta.touched) && meta.error;
    return (
        <div
            className={`${className} input${labelActive !== false ? " input--active" : ""}${
                fieldAttr.required === true ? " input--required" : ""
            }${fieldProps.icon || fieldProps.iconText ? ` input--has-icon` : ""}${
                fieldProps.iconPosition
                    ? ` input--icon-${fieldProps.iconPosition}`
                    : `${fieldProps.icon || fieldProps.iconText ? " input--icon-left" : ""}`
            }${meta.active ? ` input--focused` : ""}${
                fieldAttr.disabled ? ` input--disabled` : ""
            }${hasError ? ` input--error` : ""}
            ${!fieldProps?.renderIconOnly && fieldProps.label ? ` input--hasLabel` : ""}
            ${fieldProps.columnDirection ? ` input--columnDirection` : ""}
            ${fieldProps.showErrorOnTop ? ` input--showErrorOnTop` : ""}
            ${fieldProps.clearable ? "input--clearable" : ""}
            `}
        >
            {!fieldProps?.renderIconOnly && fieldProps.label && (
                <label
                    htmlFor={input.name}
                    className={`input__label ${
                        labelActive !== false ? " input__label--active" : ""
                    }`}
                >
                    {fieldProps.label}
                </label>
            )}
            {fieldAttr?.fieldTooltip ? (
                <Tooltip overlay={fieldAttr?.fieldTooltip}>{children}</Tooltip>
            ) : (
                children
            )}

            {(fieldProps.icon || fieldProps.iconText) && (
                <div
                    className={`input__icon${
                        fieldProps.iconPosition === "right"
                            ? ` input__icon--right`
                            : ` input__icon--left`
                    }`}
                >
                    {fieldProps.icon && <Icon icon={fieldProps.icon} />}
                    {fieldProps.iconText && (
                        <span className="input__icon-text">{fieldProps.iconText}</span>
                    )}
                </div>
            )}
            {fieldProps.clearable && !fieldAttr.disabled && hasValue && (
                <span
                    role="button"
                    aria-label={`Clear ${input.name}`}
                    tabIndex={-1}
                    className="input__action input__action--clear"
                    onKeyPress={() => input.onChange("")}
                    onClick={() => input.onChange("")}
                >
                    <Icon icon={IconCollection.close} />
                </span>
            )}
            {hasError && (
                <span className="input__helper-text input__helper-text--error">
                    {meta.error.message ? meta.error.message : meta.error}
                </span>
            )}
            {fieldProps.helperText && (
                <span className="input__helper-text">{fieldProps.helperText}</span>
            )}
            {!fieldProps?.renderIconOnly && fieldProps.actionButtons && (
                <div className="form__actionButtons">{fieldProps.actionButtons}</div>
            )}
            {!fieldProps?.renderIconOnly && fieldProps.docs && (
                <div className="form__docs">
                    <DocsInfoTooltip
                        title={fieldProps.label}
                        text={fieldProps.docs?.text}
                        urlPath={fieldProps.docs?.urlPath ?? fieldProps.docs?.url}
                    />
                </div>
            )}
        </div>
    );
}
